import { createFileRoute, redirect, useParams } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LangProvider } from "../contexts/LangContext";
import { ApiPage } from "../pages/ApiPage";
import { buildCompleteHead } from "../utils/seo";

const allowedLanguages = ["oz", "uz", "ru", "en"];

export const Route = createFileRoute("/$lang/uzbek-transliteration-api")({
  head: ({ params }) => {
    const currentPath = `/${params.lang}/uzbek-transliteration-api`;
    const canonicalUrl = `https://lotin.uz${currentPath}`;
    const seoData = buildCompleteHead(
      params.lang as "oz" | "uz" | "ru",
      "home",
      canonicalUrl,
      currentPath,
      currentPath,
    );

    return {
      meta: [{ title: seoData.title }, ...seoData.meta],
      links: seoData.links,
    };
  },
  // Cast as any to satisfy older `beforeLoad` typing that expects `never`
  beforeLoad: ((ctx: { params: { lang: string } }) => {
    const { params } = ctx;
    if (!allowedLanguages.includes(params.lang)) {
      throw redirect({ to: "/uzbek-transliteration-api" });
    }
    if (params.lang === "oz") {
      throw redirect({ to: "/uzbek-transliteration-api" });
    }
  }) as any,
  component: UzbekTransliterationApi,
});

function UzbekTransliterationApi() {
  const { lang } = useParams({ from: "/$lang/uzbek-transliteration-api" });
  const { i18n } = useTranslation();

  useEffect(() => {
    const langMap: Record<string, string> = {
      oz: "uz-Latn",
      uz: "uz-Cyrl",
      ru: "ru",
      en: "en-US",
    };
    document.documentElement.lang = langMap[lang] || "uz-Latn";
    // Guard for SSR: only call changeLanguage if it exists
    if (i18n?.changeLanguage) {
      i18n.changeLanguage(lang);
    }

    const titleMap: Record<string, string> = {
      oz: "API Reference — Lotin.uz",
      uz: "API Reference — Lotin.uz",
      ru: "API Reference — Lotin.uz",
      en: "API Reference — Lotin.uz",
    };
    document.title = titleMap[lang] || titleMap.oz;
  }, [lang, i18n]);

  const langMapForContext: Record<string, string> = {
    oz: "uz-Latn",
    uz: "uz-Cyrl",
    ru: "ru",
    en: "en-US",
  };

  return (
    <LangProvider lang={langMapForContext[lang] || "uz-Latn"}>
      <ApiPage lang={langMapForContext[lang]} />
    </LangProvider>
  );
}
